<template>
  <div class="ama-inline-validation my-3">
    <AddAmaDialog
      v-model="addAmaOpen"
      :participant="addAmaParticipant"
      :initialAmaNumber="addAmaInitialNumber"
      :validationDate="addAmaValidationDate"
      :buttonColor="buttonColor"
      @added="onAmaAdded"
      @cancelled="onAmaAddCancelled"
    />

    <WarningAlert v-if="shouldShow">
      <p class="font-weight-bold mb-0">{{ $t("memberRegistration.ama_inline.title") }}</p>
      <p class="mb-0">{{ $t("memberRegistration.ama_step.subtitle", { organizationName: orgDisplayName }) }}</p>
    </WarningAlert>

    <v-data-table
      :headers="headers"
      :items="rows"
      :items-per-page="-1"
      dense
      :footer-props="{
        'items-per-page-text': $t('dataTable.rowsPerPageText'),
        'items-per-page-all-text': $t('dataTable.rowsPerPageAllText')
      }"
      :no-data-text="$t('dataTable.noDataText')"
      :headerProps="{ 'sort-by-text': $t('dataTable.sortByText') }"
    >
      <template v-slot:[`item.amaNumber`]="{ item }">
        <span v-if="item.amaNumber">{{ item.amaNumber }}</span>
        <span v-else class="text--secondary">—</span>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <span v-if="item.status === 'loading'" class="text--secondary">
          {{ $t("memberRegistration.ama_inline.ama_status_loading_short") }}
        </span>
        <span v-else-if="item.status === 'valid'" style="color: green;">
          {{ $t("memberRegistration.ama_inline.ama_status_valid_short") }}
          <span v-if="item.membershipExpiration" class="text--secondary" style="font-weight: normal;">
            ({{ formatExpiration(item.membershipExpiration) }})
          </span>
        </span>
        <span v-else-if="item.status === 'expired'" style="color: #b71c1c; font-weight: bold;">
          {{ $t("memberRegistration.ama_inline.ama_status_expired_short") }}
          <span v-if="item.membershipExpiration" style="font-weight: normal;">
            ({{ formatExpiration(item.membershipExpiration) }})
          </span>
        </span>
        <span v-else-if="item.status === 'invalid'" style="color: #b71c1c; font-weight: bold;">
          {{ $t("memberRegistration.ama_inline.ama_status_invalid_short") }}
        </span>
        <span v-else-if="item.status === 'mismatch'" style="color: #b71c1c; font-weight: bold;">
          {{ $t("memberRegistration.ama_inline.ama_status_mismatch_short") }}
        </span>
        <span v-else class="text--secondary">
          {{ $t("memberRegistration.ama_inline.ama_status_unknown_short") }}
        </span>
      </template>

      <template v-slot:[`item.waiverStatus`]="{ item }">
        <span v-if="item.bypass" style="color: green;">
          {{ $t("memberRegistration.ama_inline.waiver_signed") }}
        </span>
        <span v-else style="color: #b71c1c; font-weight: bold;">
          {{ $t("memberRegistration.ama_inline.waiver_missing") }}
        </span>
      </template>

      <template v-slot:[`item.action`]="{ item }">
        <v-btn
          v-if="!item.amaNumber || item.status !== 'valid'"
          x-small
          :color="buttonColor || 'primary'"
          class="white--text"
          @click="addAma(item.participant, item.amaNumber)"
        >
          {{ $t("memberRegistration.ama_inline.manage_ama") }}
        </v-btn>
      </template>
    </v-data-table>

    <v-alert
      v-if="hasAnyMismatch"
      type="warning"
      dense
      outlined
      class="mt-3"
    >
      {{ $t("memberRegistration.ama_inline.mismatch_hint") }}
    </v-alert>
  </div>
</template>

<script>
import axios from "axios";
import AddAmaDialog from "@/components/memberRegistration/AddAmaDialog.vue";
import WarningAlert from "@/components/alert/WarningAlert";
import { VDataTable, VBtn, VAlert } from "vuetify/lib";

export default {
  name: "AmaInlineValidation",
  components: { AddAmaDialog, WarningAlert, VDataTable, VBtn, VAlert },
  props: {
    participants: { type: Array, default: () => [] },
    membershipType: { type: Object, default: null },
    itemOrg: { type: Object, default: null },
    buttonColor: { type: String, default: "primary" }
  },
  data() {
    return {
      loadingMap: {},
      verificationsByUser: {},
      mismatchByUser: {},
      amaNumbersByUser: {},
      addAmaOpen: false,
      addAmaParticipant: null,
      addAmaInitialNumber: null,
      addAmaValidationDate: null
    };
  },
  computed: {
    shouldShow() {
      return !!(this.membershipType && Number(this.membershipType.requiresValidAma) === 1);
    },
    hasAnyMismatch() {
      return Object.values(this.mismatchByUser).some(Boolean);
    },
    orgDisplayName() {
      if (!this.itemOrg) return "";
      return this.itemOrg.orgName || this.itemOrg.name || "";
    },
    headers() {
      return [
        { text: this.$t("memberRegistration.ama_inline.col_first_name"), value: "fName", sortable: false },
        { text: this.$t("memberRegistration.ama_inline.col_last_name"), value: "lName", sortable: false },
        { text: this.$t("memberRegistration.ama_inline.col_ama_number"), value: "amaNumber", sortable: false },
        { text: this.$t("memberRegistration.ama_inline.col_ama_status"), value: "status", sortable: false },
        { text: this.$t("memberRegistration.ama_inline.col_ama_waiver_status"), value: "waiverStatus", sortable: false },
        { text: this.$t("memberRegistration.ama_inline.col_actions"), value: "action", sortable: false, align: "end" }
      ];
    },
    rows() {
      return (this.participants || []).map(p => {
        const pid = p && (p.userId || p.id);
        const row = this.verificationsByUser[pid];
        return {
          pid,
          participant: p,
          fName: p && p.fName,
          lName: p && p.lName,
          amaNumber: this.participantAma(p),
          status: this.statusFor(p),
          bypass: this.bypassAppliesFor(p),
          // Surface the AMA API's membership expiration so the status
          // column can annotate Valid / Expired rows with the date.
          membershipExpiration: row ? row.membershipExpiration : null
        };
      });
    }
  },
  watch: {
    shouldShow: {
      handler(val) {
        if (val) this.loadBulk();
      },
      immediate: true
    },
    participants: {
      handler() {
        if (this.shouldShow) this.loadBulk();
      },
      deep: true
    }
  },
  methods: {
    refresh() {
      this.loadBulk();
    },
    /**
     * Renders an AMA membershipExpiration (ISO date or datetime) as a
     * short human date. Uses the global formatDateForHumans filter when
     * available so the format matches the rest of the app, falls back
     * to toLocaleDateString otherwise.
     */
    formatExpiration(value) {
      if (!value) return "";
      const filter = this.$options.filters && this.$options.filters.formatDateForHumans;
      if (typeof filter === "function") {
        return filter(value, false);
      }
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString();
    },
    participantAma(participant) {
      if (!participant || !(participant.userId || participant.id)) return null;
      const fromCanonical = this.amaNumbersByUser[(participant.userId || participant.id)];
      return fromCanonical ? fromCanonical : null;
    },
    statusFor(participant) {
      if (!participant) return "unknown";
      const pid = participant.userId || participant.id;
      if (this.loadingMap[pid]) return "loading";
      // Without an AMA number on file, the participant cannot be valid —
      // a stale verification cache row must not claim otherwise.
      if (!this.participantAma(participant)) return "unknown";
      if (this.mismatchByUser[pid]) return "mismatch";
      const row = this.verificationsByUser[pid];
      if (!row) return "unknown";
      if (this.hasValidMembership(row)) return "valid";
      // Distinguish "expired" (had a membership, it lapsed) from the
      // catch-all "invalid" (no AMA API match, revoked, other reasons).
      // Expired = membershipExpiration is present and in the past.
      if (row.membershipExpiration) {
        const exp = new Date(row.membershipExpiration);
        if (!Number.isNaN(exp.getTime()) && exp.getTime() < Date.now()) {
          return "expired";
        }
      }
      return "invalid";
    },
    hasValidMembership(row) {
      return !!(row && row.activeMembership);
    },
    bypassAppliesFor(participant) {
      if (!participant) return false;
      // No AMA number on file means no release on file — stale cache rows
      // must not drive waiver bypass.
      if (!this.participantAma(participant)) return false;
      const row = this.verificationsByUser[(participant.userId || participant.id)];
      if (!row) return false;
      const age = this.ageOf(participant);
      const adultAge = Number(this.itemOrg && this.itemOrg.adultAge) > 0 ? Number(this.itemOrg.adultAge) : 18;
      const isMinor = age !== null && age < adultAge;
      if (isMinor) {
        return Boolean(row.minorReleaseCurrentYear) && Boolean(row.activeMembership);
      }
      const releaseYear = row.releaseExpiration ? String(row.releaseExpiration).slice(0, 4) : null;
      const currentYear = new Date().getFullYear();
      return (
        Boolean(row.activeMembership) &&
        String(row.releaseType || "").toLowerCase() === "adult" &&
        releaseYear !== null &&
        Number(releaseYear) >= currentYear
      );
    },
    ageOf(participant) {
      const dob = participant.birthday || participant.dateOfBirth;
      if (!dob) return null;
      const d = new Date(dob);
      if (Number.isNaN(d.getTime())) return null;
      const now = new Date();
      let age = now.getFullYear() - d.getFullYear();
      const m = now.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
      return age;
    },
    toIsoDate(value) {
      if (!value) return null;
      const raw = String(value);
      const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
      if (match) return match[1];
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return null;
      return parsed.toISOString().slice(0, 10);
    },
    selectedDailyEventDate(participant) {
      if (!participant || !this.membershipType || Number(this.membershipType.periodType) !== 1) {
        return null;
      }
      const events = Array.isArray(this.membershipType.events) ? this.membershipType.events : [];
      const defaultEventId = events.length === 1 && events[0] ? events[0].id : 0;
      const selectedEventId = Number(participant.membershipEventId || defaultEventId);
      const event = events.find((item) => Number(item.id) === selectedEventId);
      return event ? this.toIsoDate(event.eventDate) : null;
    },
    amaValidationDate(participant) {
      return this.selectedDailyEventDate(participant) || new Date().toISOString().slice(0, 10);
    },
    shouldVerifyParticipant(participant) {
      const pid = participant && (participant.userId || participant.id);
      if (!pid || !this.participantAma(participant) || this.loadingMap[pid]) {
        return false;
      }
      const isDailyMembership = Number(this.membershipType && this.membershipType.periodType) === 1;
      return isDailyMembership || !this.verificationsByUser[pid];
    },
    async loadBulk() {
      const ids = (this.participants || []).map(p => Number(p.userId || p.id)).filter(Boolean);
      if (!ids.length) return;
      const apiBase = this.$store.getters.env.get('VUE_APP_API_LINK');
      try {
        const resp = await axios.post(`${apiBase}/participant/membership/ama/numbers`, { userIds: ids });
        this.amaNumbersByUser = (resp && resp.data && resp.data.success === 1) ? (resp.data.message || {}) : {};
      } catch (e) {
        this.amaNumbersByUser = {};
      }
      try {
        const resp = await axios.post(`${apiBase}/participant/membership/ama/verifications`, { userIds: ids });
        if (resp && resp.data && resp.data.success === 1) {
          this.verificationsByUser = resp.data.message || {};
        }
      } catch (e) {
        this.verificationsByUser = {};
      }
      this.participants.forEach(p => {
        if (this.shouldVerifyParticipant(p)) {
          this.verify(p);
        }
      });
      this.emitState();
    },
    async verify(participant) {
      const amaNumber = this.participantAma(participant);
      if (!amaNumber) {
        this.$emit("add-ama", participant);
        return;
      }
      const pid = participant.userId || participant.id;
      this.$set(this.loadingMap, pid, true);
      try {
        const orgId = this.itemOrg && (this.itemOrg.id || this.itemOrg.orgId);
        const apiBase = this.$store.getters.env.get('VUE_APP_API_LINK');
        const validationDate = this.amaValidationDate(participant);
        const response = await axios.post(`${apiBase}/participant/membership/ama/verify`, {
          userId: pid,
          amaNumber,
          organizationId: orgId,
          date: validationDate
        });
        if (response && response.data && response.data.success === 1) {
          const payload = response.data.message || {};
          if (payload.errors && payload.errors.length) {
            this.$set(this.mismatchByUser, pid, true);
            this.$emit("mismatch", { participant, payload });
          } else if (payload.verification) {
            this.$set(this.verificationsByUser, pid, payload.verification);
            this.$set(this.mismatchByUser, pid, false);
          }
        }
      } catch (e) {
        this.$set(this.mismatchByUser, pid, true);
      } finally {
        this.$set(this.loadingMap, pid, false);
        this.emitState();
      }
    },
    addAma(participant, initialAmaNumber) {
      // Single "Manage AMA" entry point: pre-fills with the participant's
      // existing AMA number when there is one so the user can correct a
      // typo, renew, or replace it without losing context. Called with
      // null for participants who have no number on file (legacy "Add").
      this.addAmaParticipant = participant;
      this.addAmaInitialNumber = initialAmaNumber || null;
      this.addAmaValidationDate = this.selectedDailyEventDate(participant);
      this.addAmaOpen = true;
    },
    async onAmaAdded(payload) {
      if (payload && payload.participant && payload.amaNumber) {
        const pid = Number(payload.participant.userId || payload.participant.id);
        if (pid) this.$set(this.amaNumbersByUser, pid, payload.amaNumber);
      }
      await this.loadBulk();
      const addedPid = payload && payload.participant && (payload.participant.userId || payload.participant.id);
      if (addedPid && !this.loadingMap[addedPid] && !this.verificationsByUser[addedPid]) {
        this.verify(payload.participant);
      }
      this.emitState();
    },
    onAmaAddCancelled() {},
    emitState() {
      this.$emit("state", {
        verifications: this.verificationsByUser,
        mismatches: this.mismatchByUser,
        allValid: (this.participants || []).every(p => this.statusFor(p) === "valid")
      });
    }
  }
};
</script>
